import { Component, ElementRef, inject, Type, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import { Booking, CheckoutParams, Review } from '@luggagehero/shared/interfaces';
import { SharedErrorService } from '@luggagehero/shared/services/error';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import { SharedReviewsService } from '@luggagehero/shared/services/reviews';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { SharedUiConfirmButtonComponent } from '@luggagehero/shared/ui';
import { pickedUpInstructionKey } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';

import { DateUtil } from '../../../../../utils/date.util';
import { NumberUtil } from '../../../../../utils/number.util';
import { StringUtil } from '../../../../../utils/string.util';
import { getEffectiveExternalReviewUrl } from '../../../../../utils/url.validators';
import { ReviewBaseComponent } from '../../review.base-component';
import { BookingActionBaseComponent } from './booking-action.base-component';
import { BookingActionInfo } from './booking-action-info';
import { ShowReceiptActionBaseComponent } from './show-receipt-action.base-component';
import { ValidateBookingActionBaseComponent } from './validate-booking-action.base-component';

/**
 * AM-4364 — the pickup-action modal hosts the full pickup + review flow:
 *
 *   tipping     →  picked-up   →  comment     →  google-cta  →  done
 *   (tip+CTA)      (banner+        (textarea+     (Google link
 *                   stars)          Submit/Skip)   + Skip)
 *
 * `tipping` is the legacy state; everything else is gated on the
 * `AppConfig.EXPERIMENT_VARIANTS.pickupReviewInPickupModal` variant (resolved
 * by `SharedSplitTestingService` from URL query or backend appsettings). When
 * the variant isn't active, the modal closes on PAID and the legacy
 * `showPickupConfirmationActionInfo` flow runs.
 */
export type PickupFlowStep = 'tipping' | 'picked-up' | 'comment' | 'google-cta' | 'done';

interface TipOptions {
  amount: number;
  label: string;
}

// Used for calculating tip options when we cannot get info via order or pricing
const DEFAULT_BOOKING_TOTAL = 20; // 20 eur/gbp/usd
const DEFAULT_HOURLY_RATE = 2; // 2 eur/gbp/usd

const DEFAULT_BOOKING_TOTAL_AUD = DEFAULT_BOOKING_TOTAL * AppConfig.AUD_TIP_CONVERSION_RATE;
const DEFAULT_HOURLY_RATE_AUD = DEFAULT_HOURLY_RATE * AppConfig.AUD_TIP_CONVERSION_RATE;

const DEFAULT_BOOKING_TOTAL_CAD = DEFAULT_BOOKING_TOTAL * AppConfig.CAD_TIP_CONVERSION_RATE;
const DEFAULT_HOURLY_RATE_CAD = DEFAULT_HOURLY_RATE * AppConfig.CAD_TIP_CONVERSION_RATE;

const DEFAULT_BOOKING_TOTAL_DKK = DEFAULT_BOOKING_TOTAL * AppConfig.DKK_TIP_CONVERSION_RATE;
const DEFAULT_HOURLY_RATE_DKK = DEFAULT_HOURLY_RATE * AppConfig.DKK_TIP_CONVERSION_RATE;

const DEFAULT_BOOKING_TOTAL_SEK = DEFAULT_BOOKING_TOTAL * AppConfig.SEK_TIP_CONVERSION_RATE;
const DEFAULT_HOURLY_RATE_SEK = DEFAULT_HOURLY_RATE * AppConfig.SEK_TIP_CONVERSION_RATE;

const DEFAULT_BOOKING_TOTAL_NOK = DEFAULT_BOOKING_TOTAL * AppConfig.NOK_TIP_CONVERSION_RATE;
const DEFAULT_HOURLY_RATE_NOK = DEFAULT_HOURLY_RATE * AppConfig.NOK_TIP_CONVERSION_RATE;

const TIP_FACTOR_LG = 5;
const TIP_FACTOR_MD = 3;
const TIP_FACTOR_SM = 1;

@Component({
  template: '',
  standalone: false,
})
export abstract class PickUpActionBaseComponent extends BookingActionBaseComponent {
  private translate = inject(TranslateService);
  private error = inject(SharedErrorService);
  private notify = inject(SharedNotificationService);
  private priceService = inject(SharedPricingService);
  private reviewsService = inject(SharedReviewsService);
  private featureResolver = inject(FeatureResolver);
  protected windowService = inject(SharedWindowService);

  @ViewChild('customTipInput') public customTipInput: ElementRef<HTMLInputElement>;
  @ViewChild('customTip') public customTipModel: NgModel;
  @ViewChild('confirmButton') public confirmButton: SharedUiConfirmButtonComponent;
  public isCustomTipVisible = false;
  public isSlideToConfirmEnabled = AppConfig.IS_SLIDE_TO_CONFIRM_ENABLED;
  public showExpandedImage = false;

  private currency = 'EUR';
  private _tip: number;
  private _readyToCollect = false;
  private _isPaymentMethodChanged = false;
  private _isConfirmPickupClicked = false;
  public tipOptions: TipOptions[] = [];
  public customTipValue: number;

  // AM-4364 — pickup flow step machine. `tipping` is the entry state; other steps
  // only show after pickup is confirmed AND the `pickupReviewInPickupModal` variant is active.
  private _currentStep: PickupFlowStep = 'tipping';
  private _rating: number | null = null;

  get showPickupConfirmationActionInfo(): BookingActionInfo {
    return new BookingActionInfo('ShowPickUpConfirmationFromDropOffConfirmation')
      .withCallToAction(this._translate('SHOW_PICKUP_CONFIRMATION_LONG'))
      .withAction({ componentToShow: this.validateBookingActionComponentType })
      .withTitle(null)
      .withState(this.booking.paidDirectly ? 'warning' : 'success');
  }

  get retryPaymentActionInfo(): BookingActionInfo {
    let actionInfo = new BookingActionInfo('RetryPaymentFromDropOffConfirmation')
      .withCallToAction(this._translate('RETRY_PAYMENT_BUTTON'))
      .withAction({ componentToShow: this.showReceiptActionComponentType })
      .withTitle(this._translate('PAYMENT_FAILED'))
      .withState('error')
      .withBookingChangeHandler((b) => this.onRetryPayment(b));

    if (this.booking.paymentPostponedAt) {
      actionInfo = actionInfo.withSkipAction({
        functionToCall: () => {
          this.actionRequest.emit(this.showPickupConfirmationActionInfo);
          return Promise.resolve(null);
        },
      });
    }

    return actionInfo;
  }

  get currencySymbol(): string {
    return StringUtil.getCurrencySymbol(this.currency);
  }

  get luggageImage(): string {
    if (!this.booking?.luggage?.smartImages?.length) {
      return null;
    }
    if (!AppConfig.SHOPS_WITH_LUGGAGE_IMAGE_BEFORE_PICKUP.includes(this.booking.shopId)) {
      return null;
    }
    return this.booking.luggage.smartImages[0].name;
  }

  get isStorageTimerEnabled(): boolean {
    return AppConfig.IS_STORAGE_TIMER_IN_PICKUP_ACTION_ENABLED;
  }

  get tip(): number {
    return this._tip;
  }
  set tip(value: number) {
    if (typeof value === 'string') {
      // The binding to the input element gives us a string value, so we need to convert it to a number
      value = value && !isNaN(value) ? Number(value) : undefined;
    }
    this._tip = value;
    this.cd.markForCheck();
  }

  get readyToCollect(): boolean {
    return this._readyToCollect;
  }
  set readyToCollect(value: boolean) {
    this._readyToCollect = value;
    this.cd.markForCheck();
  }

  get isPaymentMethodChanged(): boolean {
    return this._isPaymentMethodChanged;
  }
  set isPaymentMethodChanged(value: boolean) {
    this._isPaymentMethodChanged = value;
    this.cd.markForCheck();
  }

  get isConfirmPickupClicked(): boolean {
    return this._isConfirmPickupClicked;
  }
  set isConfirmPickupClicked(value: boolean) {
    this._isConfirmPickupClicked = value;
    this.cd.markForCheck();
  }

  get isMissingTipSelection(): boolean {
    if (!this.isConfirmPickupClicked) {
      return false;
    }
    return !this.canStopTimer;
  }

  get isCustomTipInvalid(): boolean {
    if (!this.isConfirmPickupClicked) {
      return false;
    }
    if (!this.isCustomTipVisible) {
      return false;
    }
    return this.customTipModel.invalid || this.tip === undefined;
  }

  get isTipTooLarge(): boolean {
    if (!this.isConfirmPickupClicked) {
      return false;
    }
    if (!AppConfig.IS_BIG_TIP_DISABLED) {
      return false;
    }
    return this.tip > this.maxTip;
  }

  get showTip(): boolean {
    if (this.maxTip < 1) {
      return false;
    }
    if (this.booking.isZeroPaymentBooking) {
      return false;
    }

    return this.shop.isPickUpTipEnabled && AppConfig.IS_TIPPING_ENABLED;
  }

  get estimatedTotal(): number {
    // Make sure we have an estimated total (use defaults if not available)

    const currencyEstimatedTotals: { [key: string]: number } = {
      AUD: DEFAULT_BOOKING_TOTAL_AUD,
      CAD: DEFAULT_BOOKING_TOTAL_CAD,
      DKK: DEFAULT_BOOKING_TOTAL_DKK,
      SEK: DEFAULT_BOOKING_TOTAL_SEK,
      NOK: DEFAULT_BOOKING_TOTAL_NOK,
    };

    return NumberUtil.valid(
      this.booking?.price?.estimated?.total,
      currencyEstimatedTotals[this.latestOrder.currency.toUpperCase()] || DEFAULT_BOOKING_TOTAL,
    );
  }

  get hourlyRate(): number {
    const currencyHourlyRates: { [key: string]: number } = {
      AUD: DEFAULT_HOURLY_RATE_AUD,
      CAD: DEFAULT_HOURLY_RATE_CAD,
      DKK: DEFAULT_HOURLY_RATE_DKK,
      SEK: DEFAULT_HOURLY_RATE_SEK,
      NOK: DEFAULT_HOURLY_RATE_NOK,
    };

    // Make sure we have an hourly rate (use defaults if not available)
    return NumberUtil.valid(
      this.getHourlyRateUnitPrice(),
      currencyHourlyRates[this.latestOrder.currency.toUpperCase()] || DEFAULT_HOURLY_RATE,
    );
  }

  get tipLimit(): number {
    const currencyTipLimits: { [key: string]: number } = {
      AUD: AppConfig.TIP_LIMIT_AUD,
      CAD: AppConfig.TIP_LIMIT_CAD,
      DKK: AppConfig.TIP_LIMIT_DKK,
      SEK: AppConfig.TIP_LIMIT_SEK,
      NOK: AppConfig.TIP_LIMIT_NOK,
    };

    let limit = currencyTipLimits[this.latestOrder.currency.toUpperCase()] || AppConfig.TIP_LIMIT;
    if (DateUtil.minutesSinceCheckIn(this.booking) < 60) {
      limit /= 10; // Lower limit if booking was checked in less than an hour ago
    }
    return limit;
  }

  get maxTip(): number {
    if (this.estimatedTotal === 0) {
      // Don't allow tips for free bookings
      return 0;
    }

    if (this.booking.orderDetails && this.booking.orderDetails[0].tip?.maxTip > 0) {
      return this.booking.orderDetails[0].tip.maxTip;
    }

    // Get the biggest of the two ways to calculate the max tip
    const maxTip = Math.max(
      this.estimatedTotal * AppConfig.MAX_TIP_FACTOR_ESTIMATED_TOTAL,
      this.hourlyRate * AppConfig.MAX_TIP_FACTOR_HOURLY_RATE,
    );

    // Apply tip limit if it's lower than the calculated max tip and return the rounded value
    return Math.round(Math.min(maxTip, this.tipLimit));
  }

  get maxTipFormatted(): string {
    return this.priceService.format(this.maxTip, this.currency);
  }

  get canStopTimer(): boolean {
    if (this.isLoading) {
      return false;
    }
    if (!this.isConfirmPickupClicked) {
      return true;
    }
    if (this.booking.isZeroPaymentBooking) {
      return true;
    }

    if (!this.showTip) {
      return true;
    }
    if (this.tip === undefined) {
      return false;
    }
    if (this.tip > this.maxTip && AppConfig.IS_BIG_TIP_DISABLED) {
      return false;
    }
    return true;
  }

  // ===========================================================================
  // AM-4364 — multi-step pickup flow (tipping → picked-up → comment → google-cta)
  // ===========================================================================

  /** Current step of the pickup-action modal's internal state machine. */
  public get currentStep(): PickupFlowStep {
    return this._currentStep;
  }

  /** Force-set the step. Exposed for stories + tests; not for production callers. */
  public set currentStep(value: PickupFlowStep) {
    this._currentStep = value;
    this.cd.markForCheck();
  }

  /** Current rating (1-5 or null). Set via `onRatingChange` when the user taps a star. */
  public get rating(): number | null {
    return this._rating;
  }
  public set rating(value: number | null) {
    this._rating = value;
    this.cd.markForCheck();
  }

  /**
   * Whether the in-modal review flow (Variant A) is active for this booking.
   *
   * AM-4364 — delegates to `FeatureResolver`, which resolves the effective flow from the
   * `pickupReviewFlow` feature flag first (a concrete value there is "the new normal" and wins
   * for everyone) and otherwise from the temporary per-user experiment variant. At most one
   * flow is active at a time.
   */
  public get isReviewFlowEnabled(): boolean {
    return this.featureResolver.pickupReviewFlow === 'in-pickup-modal';
  }

  /** Effective GBP/Maps URL used by the 4-5★ Google review CTA. */
  public get effectiveExternalReviewUrl(): string {
    return getEffectiveExternalReviewUrl(this.shop);
  }

  /**
   * Whether the user has typed anything into the comment textarea. Drives the
   * "Submit review" CTA's enabled state — Submit is for users who wrote text;
   * users with no comment use "No thanks, rating is enough" instead.
   */
  public get hasComment(): boolean {
    return !!this.review?.headline?.trim();
  }

  /** Whether the Google review CTA should appear (4-5★ + URL available). */
  public get showGoogleReviewCta(): boolean {
    if (!this.effectiveExternalReviewUrl) {
      return false;
    }
    if (AppConfig.IS_EXTERNAL_REVIEW_DISABLED) {
      return false;
    }
    return (this._rating ?? 0) >= 4;
  }

  /**
   * User tapped a star. Persist immediately, derive satisfaction from the rating,
   * and transition to the `comment` step so they can optionally add text.
   */
  public async onRatingChange(value: number): Promise<void> {
    this._rating = value;

    try {
      if (!this.review) {
        this.review = this.buildReviewStub();
      }
      this.review.rating = value;
      this.review.satisfaction = ReviewBaseComponent.starsToSatisfaction(value);

      const saved = this.review._id
        ? await this.reviewsService.updateReview(this.review)
        : await this.reviewsService.addReview(this.review);

      this.review = saved;
      this._currentStep = 'comment';
      this.cd.markForCheck();
    } catch (err) {
      this.error.handleError(err, this.translate.instant('UNEXPECTED_ERROR') as string);
    }
  }

  /**
   * User tapped "Submit review" (or "No thanks, rating is enough") on the comment
   * screen. Publish the review and route to either the Google CTA or close the modal.
   */
  public async submitReview(): Promise<void> {
    try {
      if (!this.review) {
        this.review = this.buildReviewStub();
      }
      this.review.published = true;
      const saved = this.review._id
        ? await this.reviewsService.updateReview(this.review)
        : await this.reviewsService.addReview(this.review);
      this.review = saved;

      if (this.showGoogleReviewCta) {
        this._currentStep = 'google-cta';
        this.cd.markForCheck();
      } else {
        this.completeFlow();
      }
    } catch (err) {
      this.error.handleError(err, this.translate.instant('UNEXPECTED_ERROR') as string);
    }
  }

  /** User tapped "Leave Google review" on the cross-pollination prompt. */
  public openGoogleReview(): void {
    if (this.effectiveExternalReviewUrl) {
      this.windowService.open(this.effectiveExternalReviewUrl, '_blank');
    }
    this.completeFlow();
  }

  /** User tapped "Skip" on the Google CTA, or any other "no thanks" terminal action. */
  public skipGoogleReview(): void {
    this.completeFlow();
  }

  /** Finalize the modal: close + emit the legacy post-pickup confirmation action. */
  private completeFlow(): void {
    this._currentStep = 'done';
    this.close();
    if (this.booking?.status === 'PAID') {
      this.actionRequest.emit(this.showPickupConfirmationActionInfo);
    }
  }

  private buildReviewStub(): Review {
    return {
      bookingId: this.booking._id,
      shopId: this.booking.shopId,
      userName: this.booking.userName,
      userPicture: this.booking.userPicture,
      shopName: this.booking.shopName,
      published: false,
    } as Review;
  }

  /** AM-4825 — under the "Picked up!" banner: no staff to show anything to at a self-service shop. */
  get pickedUpInstructionKey(): string {
    return pickedUpInstructionKey({ isSelfService: this.shop?.isSelfService });
  }

  protected abstract get showReceiptActionComponentType(): Type<ShowReceiptActionBaseComponent>;
  protected abstract get validateBookingActionComponentType(): Type<ValidateBookingActionBaseComponent>;

  async onBaseInit() {
    if (!this.shop || !this.shopsService.latestPrice) {
      this.shop = await this.shopsService.getShopDetails(
        this.booking.shopId,
        DateUtil.serializeDate(this.booking.period.from),
        DateUtil.serializeDate(this.booking.period.to),
        this.booking.luggage.normal,
        this.booking.luggage.hand,
        this.booking.isGuest || this.booking.isWalkIn ? 'dropoff' : 'marketplace',
      );
    }

    // find the order line with one of the hourly rate keys
    const hourlyRate = this.getHourlyRateUnitPrice();

    this.currency = this.latestOrder.currency.toUpperCase();

    this.initialiseTipOptions(hourlyRate);

    if (this.booking.orderDetails && this.booking.orderDetails[0].orderLines) {
      this.booking.orderDetails[0].orderLines.forEach((orderLine) => {
        if (orderLine.product.name === 'PRICE_TIP') {
          this.tip = orderLine.unitPrice;
          if (!this.tipOptions.some((option) => option.amount === this.tip)) {
            this.customTipValue = this.tip;
            this.isCustomTipVisible = true;
          }
        }
      });
    }
  }

  private getHourlyRateUnitPrice(): number {
    const hourlyRateKeys = ['hourlyRate', 'SAME_DAY_HOURLY_RATE', 'WALK_IN_HOURLY_RATE', 'GUEST_HOURLY_RATE'];

    const hourlyRateProduct = this.latestOrder.orderLines.find((orderLine) =>
      hourlyRateKeys.includes(orderLine.product._id),
    );

    return hourlyRateProduct ? hourlyRateProduct.unitPrice : 0;
  }

  private initialiseTipOptions(hourlyRate: number) {
    if (this.booking.orderDetails && this.booking.orderDetails[0].tip) {
      this.booking.orderDetails[0].tip.options.forEach((amount) => {
        this.tipOptions.push({ amount, label: this.priceService.format(amount, this.currency) });
      });
    } else {
      // If the large tip option is too high when using the hourly rate, ceil the tip options using the max tip
      const useMaxTip = hourlyRate * TIP_FACTOR_LG > this.maxTip;

      const opt0 = 0; // Always include a 0 tip option
      const opt1 = Math.round(useMaxTip ? this.maxTip / 4 : hourlyRate * TIP_FACTOR_SM);
      const opt2 = Math.round(useMaxTip ? this.maxTip / 2 : hourlyRate * TIP_FACTOR_MD);
      const opt3 = Math.round(useMaxTip ? this.maxTip : hourlyRate * TIP_FACTOR_LG);

      this.tipOptions.push({ amount: opt0, label: this.priceService.format(opt0, this.currency) });
      this.tipOptions.push({ amount: opt1, label: this.priceService.format(opt1, this.currency) });
      this.tipOptions.push({ amount: opt2, label: this.priceService.format(opt2, this.currency) });
      this.tipOptions.push({ amount: opt3, label: this.priceService.format(opt3, this.currency) });
    }
  }

  public abstract showLuggageImage(e: Event): void;

  public async checkOut() {
    this.isConfirmPickupClicked = true;
    if (!this.canStopTimer) {
      this.confirmButton.reset();
      return;
    }

    const params: CheckoutParams = { tip: this.tip || 0 };

    const isTipValid = this.validateTip(params.tip);
    if (!isTipValid) {
      this.confirmButton.reset();
      return;
    }

    this.isLoading = true;

    try {
      const res = await this.bookingService.checkOut(this.booking._id, params);

      this.updateBooking(res);
      this.isLoading = false;
      this.isPaymentMethodChanged = false;

      // AM-4364 — when the review flow is enabled and pickup succeeded, keep the modal
      // open and transition to the `picked-up` step. Otherwise fall back to the legacy
      // behaviour of closing this modal and opening the post-pickup confirmation.
      if (this.booking.status === 'PAID' && this.isReviewFlowEnabled) {
        this._currentStep = 'picked-up';
        this.cd.markForCheck();
        return;
      }

      this.close();
      this.onCheckOut(this.booking);
    } catch (err) {
      this.isLoading = false;
      this.error.handleError(err, this.translate.instant('UNEXPECTED_ERROR') as string);
    }
  }

  public setTipAmount(value: number) {
    this.tip = value;
    this.isCustomTipVisible = false;
  }

  public toggleCustomTip() {
    this.isCustomTipVisible = !this.isCustomTipVisible;
    this.cd.markForCheck();

    if (this.isCustomTipVisible) {
      this.tip = undefined;
      setTimeout(() => this.customTipInput.nativeElement.focus(), 1);
    }
  }

  private validateTip(value: number): boolean {
    if (this.booking.isZeroPaymentBooking) {
      // No need to validate tip for zero payment bookings
      return true;
    }

    if (value <= this.maxTip) {
      // Tip is within the allowed range
      return true;
    }

    if (AppConfig.IS_BIG_TIP_DISABLED) {
      this.notify.error(`${this.translate.instant('TIP_NO_MORE_THAN_X')} ${this.maxTipFormatted}`);

      // Don't allow big tips
      return false;
    }

    // Allow big tip but ask for confirmation from the user
    try {
      const formattedTip = this.priceService.format(Math.round(value), this.currency);
      this.windowService.confirm(`${formattedTip} ${this.translate.instant('BIG_TIP_WARNING_MESSAGE')}`);
    } catch {
      // User cancelled
      return false;
    }

    // User confirmed
    return true;
  }

  private onCheckOut(booking: Booking) {
    switch (booking.status) {
      case 'CHECKED_OUT':
        this.actionRequest.emit(this.retryPaymentActionInfo);
        break;

      case 'PAID':
        console.log(`${this.constructor.name} -- onCheckOut: emitting showPickupConfirmationActionInfo`);
        this.actionRequest.emit(this.showPickupConfirmationActionInfo);
        break;
    }
  }

  private onRetryPayment(booking: Booking) {
    if (booking.status === 'PAID' || booking.paymentPostponedAt) {
      this.actionRequest.emit(this.showPickupConfirmationActionInfo);
    }
  }
}
